import type { AxiosInstance } from 'axios';
import type { paths, components } from '../schema';
import type { TimelineEntry } from '../types/timeline';
import type {
  PaginatedResponse,
  RequestOptions,
  RequestOptionsWithParams,
} from './types';
import { curry1 } from './utils';

export type MCPServerConfig = Mutable<
  paths['/api/mcp-servers/{id}']['get']['responses']['200']['content']['application/json']
>;
export type MCPServerConfigListItem = Mutable<
  components['schemas']['MCPServerConfigList']
>;
export type MCPServerConfigListResponse =
  PaginatedResponse<MCPServerConfigListItem>;
export type MCPServerConfigListParams = Required<
  paths['/api/mcp-servers']['get']['parameters']
>['query'];
type MCPServerConfigCreatePayload =
  paths['/api/mcp-servers']['post']['requestBody']['content']['application/json'];
type MCPServerConfigUpdatePayload = Required<
  paths['/api/mcp-servers/{id}']['patch']
>['requestBody']['content']['application/json'];

export type MCPServerConfigSharingSettings = NonNullable<
  components['schemas']['PatchedMCPServerConfigRequest']['sharing_settings']
>;

export type MCPTool = components['schemas']['MCPTool'];
export type MCPToolGroup = components['schemas']['MCPToolGroup'];
export type MCPToolCatalogResponse =
  components['schemas']['MCPToolCatalogResponse'];

export type MCPServer = components['schemas']['MCPServerOption'];
export type MCPServerStepOptionsParams = Required<
  paths['/api/mcp-servers/step-options']['get']['parameters']
>['query'];

export type MCPServerHistoryParams = Required<
  paths['/api/mcp-servers/{mcp_server_id}/events-history']['get']['parameters']
>['query'];

export type MCPServerHistoryEntry = TimelineEntry<
  Record<string, any>,
  'mcp_server_created' | 'mcp_server_updated' | 'mcp_server_deleted'
>;

export type MCPServerHistory = Omit<
  paths['/api/mcp-servers/{mcp_server_id}/events-history']['get']['responses']['200']['content']['application/json'],
  'results'
> & {
  results: MCPServerHistoryEntry[];
};

export const getMCPServers = async (
  instance: AxiosInstance,
  options?: RequestOptionsWithParams<MCPServerConfigListParams>
): Promise<MCPServerConfigListResponse> => {
  const res = await instance.get<MCPServerConfigListResponse>(
    `mcp-servers`,
    options
  );
  return res.data;
};

export const createMCPServer = async (
  instance: AxiosInstance,
  payload: MCPServerConfigCreatePayload,
  options?: RequestOptions
): Promise<MCPServerConfig> => {
  const res = await instance.post<MCPServerConfig>(
    `mcp-servers`,
    payload,
    options
  );
  return res.data;
};

export const getMCPServerById = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<MCPServerConfig> => {
  const res = await instance.get<MCPServerConfig>(`mcp-servers/${id}`, options);
  return res.data;
};

export const updateMCPServer = async (
  instance: AxiosInstance,
  id: string,
  payload: MCPServerConfigUpdatePayload,
  options?: RequestOptions
): Promise<MCPServerConfig> => {
  const res = await instance.patch<MCPServerConfig>(
    `mcp-servers/${id}`,
    payload,
    options
  );
  return res.data;
};

export const deleteMCPServer = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<void> => {
  await instance.delete(`mcp-servers/${id}`, options);
};

export const getMCPTools = async (
  instance: AxiosInstance,
  options?: RequestOptions
): Promise<MCPToolCatalogResponse> => {
  const res = await instance.get<MCPToolCatalogResponse>(
    `mcp-servers/tools`,
    options
  );
  return res.data;
};

export const getMCPServerStepOptions = async (
  instance: AxiosInstance,
  options?: RequestOptionsWithParams<MCPServerStepOptionsParams>
): Promise<MCPServer[]> => {
  const res = await instance.get<MCPServer[]>(
    `mcp-servers/step-options`,
    options
  );
  return res.data;
};

export const getMCPServerHistory = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptionsWithParams<MCPServerHistoryParams>
): Promise<MCPServerHistory> => {
  const res = await instance.get<MCPServerHistory>(
    `mcp-servers/${id}/events-history`,
    options
  );
  return res.data;
};

export class MCPServerService {
  public getMCPServers;
  public createMCPServer;
  public getMCPServerById;
  public updateMCPServer;
  public deleteMCPServer;
  public getMCPTools;
  public getMCPServerStepOptions;
  public getMCPServerHistory;

  constructor(private instance: AxiosInstance) {
    this.getMCPServers = curry1(getMCPServers)(this.instance);
    this.createMCPServer = curry1(createMCPServer)(this.instance);
    this.getMCPServerById = curry1(getMCPServerById)(this.instance);
    this.updateMCPServer = curry1(updateMCPServer)(this.instance);
    this.deleteMCPServer = curry1(deleteMCPServer)(this.instance);
    this.getMCPTools = curry1(getMCPTools)(this.instance);
    this.getMCPServerStepOptions = curry1(getMCPServerStepOptions)(
      this.instance
    );
    this.getMCPServerHistory = curry1(getMCPServerHistory)(this.instance);
  }
}
